import * as echarts from 'echarts';
import React, { useState, useEffect, useRef } from 'react';
import { ParsedCensusResponse, CensusData } from '@/mrRogers/interfaces';
import { DataCard } from '@/reports/index';
import { styled, useTheme, Text, Stack, Select } from '@/theme/index';

interface ChartData {
  /** Title of the piechart */
  title: string;
  /** Data from Mr.Rogers Response formatted as value and number */
  data: { value: number; name: string }[];
}
interface OwnersRentersProps {
  /** Data from Mr.Rogers Response */
  mrRogersData?: ParsedCensusResponse;
  /** Indicates if the view is in mobile. */
  isMobile?: boolean;
}

const ChartContainer = styled('div')(({ theme }) => ({
  width: '33.33%',
  height: theme.spacing(31.25),
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const ColorCircle = styled('div')(({ theme }) => ({
  width: theme.spacing(2.5),
  height: theme.spacing(2.5),
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: theme.spacing(0.6),
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  height: theme.spacing(5),
  borderRadius: theme.spacing(0.6),
  borderColor: theme.palette.primary.main,
  marginBottom: theme.spacing(2.5),
  fontSize: theme.spacing(2),
}));

/**
 * OwnersRenters is a component that will show to the user the %
 * of owners and renters on three different pie charts where the local,
 * county and state-level data is displayed.
 * This component retrieves the data from MrRogers API.
 */
export const OwnersRenters = ({
  mrRogersData,
  isMobile,
}: OwnersRentersProps) => {
  const theme = useTheme();
  const [selectedChart, setSelectedChart] = useState<number>(0);

  const chartInstances: echarts.ECharts[] = [];
  const census = {
    local: mrRogersData?.local as CensusData,
    county: mrRogersData?.county as CensusData,
    state: mrRogersData?.state as CensusData,
  };
  const colors = {
    Own: theme.palette.primary.main,
    Rent: theme.palette.secondary.main,
  };
  const mapping = {
    ohu_own_pct: {
      name: 'Own',
      color: colors.Own,
    },
    ohu_rent_pct: {
      name: 'Rent',
      color: colors.Rent,
    },
  };
  const getChartData = (censusData: CensusData) => {
    const data: { value: number; name: string }[] = [];
    for (const key in mapping) {
      if (censusData && key in censusData) {
        data.push({
          value: censusData[key as keyof CensusData] as number,
          name: mapping[key as keyof typeof mapping].name,
        });
      }
    }
    return data;
  };

  const chartData: ChartData[] = [
    {
      title: 'Local',
      data: getChartData(census.local),
    },
    {
      title: 'County',
      data: getChartData(census.county),
    },
    {
      title: 'State',
      data: getChartData(census.state),
    },
  ];

  const filteredChartData = chartData.filter(
    (dataItem) => dataItem.data.length > 0
  );

  const chartRefs: React.RefObject<HTMLDivElement>[] = Array.from(
    { length: filteredChartData.length },
    () => useRef<HTMLDivElement>(null)
  );

  const getColorForName = (name: string) => {
    const field = Object.values(mapping).find((field) => field.name === name);
    return field ? field.color : '';
  };
  useEffect(() => {
    filteredChartData.forEach((data, index) => {
      if (chartRefs[index].current) {
        const chart = echarts.init(chartRefs[index].current);

        let labelFontSize = theme.spacing(2);

        if (window.innerWidth <= 899) {
          labelFontSize = theme.spacing(2);
        }

        if (window.innerWidth <= 1024) {
          labelFontSize = theme.spacing(1.5);
        }

        chart.setOption({
          title: {
            text: data.title,
            left: 'center',
            textStyle: {
              fontSize: theme.spacing(2.5),
            },
          },
          tooltip: {
            trigger: 'item',
            confine: true,
            formatter: '{b}\n{d}%',
          },
          series: [
            {
              name: 'Data',
              type: 'pie',
              data: data.data,
              itemStyle: {
                color: (params: { data: { name: string } }) =>
                  getColorForName(params?.data?.name),
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: theme.spacing(1.25),
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                },
              },
              label: {
                show: true,
                fontSize: labelFontSize,
                position: 'inside',
                formatter: '{d}%',
              },
              labelLine: {
                show: false,
              },
            },
          ],
        });
        chartInstances.push(chart);
      }
    });
    return () => {
      chartInstances.forEach((chart) => {
        chart.dispose();
      });
    };
  }, [filteredChartData, chartRefs]);
  return (
    <DataCard>
      <Stack>
        <Text variant="h3" sx={{ marginBottom: '12px !important' }}>
          Renters vs. Owners
        </Text>
      </Stack>
      <Stack direction="column" spacing={0} sx={{ marginTop: '0 !important' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ColorCircle
            data-testid="own-color-circle"
            sx={{ backgroundColor: theme.palette.primary.main }}
          />
          <Text
            variant="subtitle1"
            sx={{ fontSize: '16px', marginLeft: '11px !important' }}
          >
            Own
          </Text>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" mt={1}>
          <ColorCircle
            data-testid="rent-color-circle"
            sx={{ backgroundColor: theme.palette.secondary.main }}
          />
          <Text
            variant="subtitle1"
            sx={{ fontSize: '16px', marginLeft: '11px !important' }}
          >
            Rent
          </Text>
        </Stack>
      </Stack>
      {isMobile ? (
        <div>
          <StyledSelect
            data-testid="ownersRenters"
            id="ownersRenters"
            options={filteredChartData.map((chart, index) => ({
              value: index.toString(),
              display: chart.title,
            }))}
            onOptionSelect={(selectedValue: string) =>
              setSelectedChart(parseInt(selectedValue))
            }
            value={selectedChart.toString()}
          />
          <ChartContainer
            ref={chartRefs[selectedChart]}
            data-testid="chart-container"
          />
        </div>
      ) : (
        <div>
          <Stack direction="row" justifyContent="center">
            {filteredChartData.map((chart, index) => (
              <ChartContainer
                key={index}
                ref={chartRefs[index]}
                data-testid="chart-container"
              />
            ))}
          </Stack>
        </div>
      )}
    </DataCard>
  );
};
